// FormattedDataTable
//
// Per-data-type "pretty" view of the loaded records, used as the alternative
// to <DataTable /> on the Analytics page. Mirrors the layout patterns we
// already use on Quick Refer / Collaboration Analytics' Formatted tab so the
// look stays consistent across the app:
//   - referrals / vitalrounds: Date, Patient, Referral For, Sending, Creating (only when ≠ Sending), Receiving, Status
//   - messages:                Date, Facility, per-channel send/receive counts
//   - studies:                 Date, Facility, Sent, Received, Total
//   - logins:                  Date, Facility, User, per-platform login counts, Total
//   - group-referrals / faxes / fax-recipients / study-details: V2.1 per-row renderers below
//   - anything else:           Date + a short JSON preview (defensive fallback)
//
// All columns are sortable. Default sort is Date desc (newest first). Click
// a column header to sort by that column; click again to flip direction.
//
// REFERRAL FIELD NAMES — see REFERRAL-FIELD-NAMING.md. We use:
//   sending_*   = the actual referring physician/facility (the patient source)
//   creating_*  = the typist who keyed the referral into VE
//   receiving_* = the destination physician/facility
// These are the canonical names DataCacheService writes to the cache after
// rewriting VitalEngine's misleading API field-name prefixes. Original API
// values are preserved as _api_created_* / _api_sending_* for forensic use.

import React, { useEffect, useMemo, useState } from 'react';
import { safeDateString } from '../utils/dateUtils';
import {
  formatDicomLabel,
  formatDocumentsLabel,
  formatTagsLabel,
  getDicomCount,
} from '../utils/referralAttachments';
import { referralTypeLabel } from '../utils/referralTypeLabel';
import { filterRecordsForTableSearch } from '../utils/tableRecordSearch';
import './FormattedDataTable.css';

const f = (r, k) => (r?.data || r?.Data || {})[k];
const ts = (r) => r?.timestamp || (r?.data || r?.Data || {}).created_date;
const tsMs = (r) => {
  const t = ts(r);
  if (!t) return 0;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

const COMPLETED_RX = /complete|closed|accepted|finished/i;
const ROWS_PER_PAGE = 200;

const LOGIN_PLATFORMS = [
  { key: 'web_login',          label: 'Web' },
  { key: 'mobile',             label: 'Mobile' },
  { key: 'desktop_login',      label: 'Desktop' },
  { key: 'goweb_login',        label: 'GoWeb' },
  { key: 'telemedicine_login', label: 'Telemed' },
  { key: 'common_login',       label: 'Common' },
];

// Per-data-type sort accessors. Keys are stable column ids we use both for
// sortField state and for the column header onClick. Anything not in this
// map falls through to a generic numeric accessor against the JSON field of
// the same name — that way per-channel / per-platform count columns
// (vchat_messages_Sent, web_login, ...) can be sorted without listing every
// possible field here.
const SORT_ACCESSORS = {
  referrals: {
    date:        tsMs,
    patient:     (r) => (f(r, 'patient_last_name') || f(r, 'patient_first_name') || '').toLowerCase(),
    referralFor: (r) => (f(r, 'referral_for') || '').toLowerCase(),
    sending:     (r) => (f(r, 'sending_user_name')  || f(r, 'sending_facility')  || '').toLowerCase(),
    creating:    (r) => (f(r, 'creating_user_name') || f(r, 'creating_facility') || '').toLowerCase(),
    receiving:   (r) => (f(r, 'receiving_user_name') || f(r, 'receiving_facility') || '').toLowerCase(),
    images:      (r) => getDicomCount(r),
    tags:        (r) => formatTagsLabel(r).toLowerCase(),
    referralType:(r) => referralTypeLabel(r).toLowerCase(),
    status:      (r) => (f(r, 'referral_status') || '').toLowerCase(),
  },
  messages: {
    date:     tsMs,
    facility: (r) => (f(r, 'facility_name') || '').toLowerCase(),
  },
  studies: {
    date:     tsMs,
    facility: (r) => (f(r, 'facility_name') || '').toLowerCase(),
    sent:     (r) => Number(f(r, 'study_sent')) || 0,
    received: (r) => Number(f(r, 'study_received')) || 0,
    total:    (r) => (Number(f(r, 'study_sent')) || 0) + (Number(f(r, 'study_received')) || 0),
  },
  logins: {
    date:     tsMs,
    facility: (r) => (f(r, 'facility_name') || '').toLowerCase(),
    user:     (r) => (f(r, 'user_display_name') || f(r, 'user_id') || '').toLowerCase(),
    total:    (r) => {
      let t = 0;
      for (const p of LOGIN_PLATFORMS) t += Number(f(r, p.key)) || 0;
      return t;
    },
  },
  generic: {
    date: tsMs,
  },
};
SORT_ACCESSORS.vitalrounds = SORT_ACCESSORS.referrals;
// 'groups' (rpt_consolidated_group_usage) was retired 2026-05; see backend
// DataTypeNames for rationale.

// V2.1 per-row types (added 2026-05). Keep these accessor maps next to the
// renderers below so columns / sort keys stay in sync.
SORT_ACCESSORS['group-referrals'] = {
  date:        tsMs,
  referralNo:  (r) => Number(f(r, 'referral_number')) || 0,
  referralFor: (r) => (f(r, 'referral_for') || '').toLowerCase(),
  group:       (r) => (f(r, 'receiving_group_name') || '').toLowerCase(),
  // Time-to-first-message in minutes; 0 when no first message yet so unset
  // rows sort to the bottom on a "newest reply" sort.
  firstMsg: (r) => {
    const created = ts(r);
    const first = f(r, 'referral_first_msg_created');
    if (!created || !first) return 0;
    const dc = new Date(created).getTime();
    const df = new Date(first).getTime();
    if (Number.isNaN(dc) || Number.isNaN(df) || df <= dc) return 0;
    return (df - dc) / 60000;
  },
  status: (r) => Number(f(r, 'referral_status')) || 0,
};
SORT_ACCESSORS.faxes = {
  date:     tsMs,
  status:   (r) => (f(r, 'fax_status') || '').toLowerCase(),
  pages:    (r) => Number(f(r, 'num_pages')) || 0,
  facility: (r) => Number(f(r, 'facility_id')) || 0,
  group:    (r) => Number(f(r, 'group_id')) || 0,
};
SORT_ACCESSORS['fax-recipients'] = {
  date:    tsMs,
  faxId:   (r) => Number(f(r, 'fax_id')) || 0,
  userId:  (r) => Number(f(r, 'user_id')) || 0,
  isRead:  (r) => Number(f(r, 'is_read')) || 0,
};
SORT_ACCESSORS['study-details'] = {
  date:        tsMs,
  description: (r) => (f(r, 'study_description') || '').toLowerCase(),
  modalities:  (r) => (f(r, 'modalities') || '').toLowerCase(),
  facility:    (r) => Number(f(r, 'facility_id')) || 0,
  user:        (r) => Number(f(r, 'user_id')) || 0,
  processType: (r) => (f(r, 'process_type') || '').toLowerCase(),
};

// Fields whose newest/largest value is the most useful first sort direction.
// Anything not in this set sorts ascending on first click (text columns).
const NUMERIC_FIRST = new Set([
  'date', 'sent', 'received', 'total', 'images',
  // V2.1 numeric columns
  'referralNo', 'firstMsg', 'pages', 'faxId', 'userId', 'isRead', 'facility', 'user',
]);

function getAccessor(dataType, sortField) {
  const map = SORT_ACCESSORS[dataType] || SORT_ACCESSORS.generic;
  if (map[sortField]) return map[sortField];
  // Fallback: treat sortField as a JSON property name on record.data and
  // sort numerically. Covers per-channel message counters and per-platform
  // login counters without us having to enumerate every field above.
  return (r) => Number(f(r, sortField)) || 0;
}

function StatusBadge({ status }) {
  if (!status) return <span className="fdt-status">—</span>;
  const s = String(status);
  const cls = COMPLETED_RX.test(s) ? 'fdt-status fdt-status-good'
    : /pending|new|open|in[ _-]?progress/i.test(s) ? 'fdt-status fdt-status-pending'
    : /declin|reject|cancel/i.test(s) ? 'fdt-status fdt-status-bad'
    : 'fdt-status';
  return <span className={cls}>{s}</span>;
}

function fmtCount(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return <span className="fdt-zero">—</span>;
  return n.toLocaleString();
}

// Safe across the Salesforce date-object shape that occasionally slips
// through unnormalized — see utils/dateUtils.js.
function fmtDate(t) {
  return safeDateString(t) || '—';
}

function SortableTh({ field, label, sortField, sortDir, onSort, className = '' }) {
  const active = sortField === field;
  const indicator = active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
  return (
    <th
      className={`fdt-sortable ${active ? 'fdt-sorted' : ''} ${className}`.trim()}
      onClick={() => onSort(field)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSort(field);
        }
      }}
      tabIndex={0}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
      title="Click to sort"
    >
      {label}{indicator}
    </th>
  );
}

function FormattedDataTable({ data, dataType, searchTerm = '', onSearchChange }) {
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');

  const searchedData = useMemo(
    () => filterRecordsForTableSearch(data, dataType, searchTerm),
    [data, dataType, searchTerm],
  );

  // Reset paging + sort whenever the underlying data (load) or data type
  // changes. Keeps the behavior intuitive: a fresh Load Data lands you on
  // page 1 sorted newest-first.
  useEffect(() => {
    setPage(1);
    setSortField('date');
    setSortDir('desc');
  }, [data, dataType]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(NUMERIC_FIRST.has(field) ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const sortedData = useMemo(() => {
    if (!searchedData || searchedData.length === 0) return [];
    const accessor = getAccessor(dataType, sortField);
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...searchedData].sort((a, b) => {
      const av = accessor(a);
      const bv = accessor(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [searchedData, dataType, sortField, sortDir]);

  const lastPage = Math.max(1, Math.ceil(sortedData.length / ROWS_PER_PAGE));
  const safePage = Math.min(Math.max(1, page), lastPage);
  const start = (safePage - 1) * ROWS_PER_PAGE;
  const pageRows = useMemo(
    () => sortedData.slice(start, start + ROWS_PER_PAGE),
    [sortedData, start]
  );

  if (!data || data.length === 0) return null;

  const searchPlaceholder = (dataType === 'referrals' || dataType === 'vitalrounds')
    ? 'Search patient, referral, provider, facility…'
    : 'Search records…';

  const sortProps = { sortField, sortDir, onSort: handleSort };

  return (
    <div className="fdt-container">
      <div className="fdt-header">
        <h3>Formatted Data</h3>
        <div className="fdt-info">
          Showing {sortedData.length === 0 ? 0 : start + 1} – {Math.min(start + ROWS_PER_PAGE, sortedData.length)} of {sortedData.length.toLocaleString()} records
          {searchTerm.trim() && data.length !== sortedData.length && (
            <span className="fdt-info-filtered"> (filtered from {data.length.toLocaleString()})</span>
          )}
        </div>
      </div>
      {onSearchChange && (
        <div className="fdt-toolbar">
          <input
            type="search"
            className="fdt-search"
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search formatted records"
          />
        </div>
      )}

      <div className="fdt-table-wrap">
        {renderForType(dataType, pageRows, sortProps)}
      </div>

      {lastPage > 1 && (
        <div className="fdt-pager">
          <button onClick={() => setPage(1)}              disabled={safePage <= 1}>« First</button>
          <button onClick={() => setPage(safePage - 1)}   disabled={safePage <= 1}>‹ Prev</button>
          <span>Page {safePage} of {lastPage}</span>
          <button onClick={() => setPage(safePage + 1)}   disabled={safePage >= lastPage}>Next ›</button>
          <button onClick={() => setPage(lastPage)}       disabled={safePage >= lastPage}>Last »</button>
        </div>
      )}
    </div>
  );
}

function renderForType(dataType, rows, sortProps) {
  if (dataType === 'referrals' || dataType === 'vitalrounds') return <ReferralRows rows={rows} sortProps={sortProps} />;
  if (dataType === 'messages')                                 return <MessageRows  rows={rows} sortProps={sortProps} />;
  if (dataType === 'studies')                                  return <StudyRows    rows={rows} sortProps={sortProps} />;
  if (dataType === 'logins')                                   return <LoginRows    rows={rows} sortProps={sortProps} />;
  if (dataType === 'group-referrals')                          return <GroupReferralRows rows={rows} sortProps={sortProps} />;
  if (dataType === 'faxes')                                    return <FaxRows           rows={rows} sortProps={sortProps} />;
  if (dataType === 'fax-recipients')                           return <FaxRecipientRows  rows={rows} sortProps={sortProps} />;
  if (dataType === 'study-details')                            return <StudyDetailRows   rows={rows} sortProps={sortProps} />;
  return <GenericRows rows={rows} sortProps={sortProps} />;
}

// ----------------------------------------------------------------------------

function ReferralRows({ rows, sortProps }) {
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"        label="Date"         {...sortProps} />
          <SortableTh field="patient"     label="Patient"      {...sortProps} />
          <SortableTh field="referralFor" label="Referral For" className="fdt-col-narrow" {...sortProps} />
          <SortableTh field="sending"     label="Sending"      {...sortProps} />
          <SortableTh field="creating"    label="Creating"     {...sortProps} />
          <SortableTh field="receiving"   label="Receiving"    {...sortProps} />
          <SortableTh field="images"      label="Images"       {...sortProps} />
          <SortableTh field="tags"        label="Tags"         {...sortProps} />
          <SortableTh field="referralType" label="Referral Type" {...sortProps} />
          <SortableTh field="status"      label="Status"       {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={10} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => {
          const fn = f(r, 'patient_first_name') || '';
          const ln = f(r, 'patient_last_name') || '';
          const dob = safeDateString(f(r, 'patient_dob'));
          const referralFor = f(r, 'referral_for') || '';

          // Sending = the actual referring physician/facility (the patient source).
          const sendUser = f(r, 'sending_user_name');
          const sendSpec = f(r, 'sending_speciality');
          const sendFac  = f(r, 'sending_facility');
          const hasSend  = sendUser || sendSpec || sendFac;

          // Creating = the typist who keyed the referral into VE. Always
          // show when any typist data exists, even when it duplicates the
          // Sending cell (i.e. the sender typed their own referral). This
          // matches VE's spreadsheet, where the Created By column is
          // populated for every row — only blank when there's truly no
          // typist info.
          const createUser = f(r, 'creating_user_name');
          const createSpec = f(r, 'creating_speciality');
          const createFac  = f(r, 'creating_facility');
          const hasCreate  = createUser || createFac;

          return (
            <tr key={`${f(r, 'referral_id') || i}-${i}`}>
              <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
              <td>
                <div className="fdt-prov-name">{(fn + ' ' + ln).trim() || '—'}</div>
                {dob && <div className="fdt-prov-meta">DOB: {dob}</div>}
              </td>
              <td className="fdt-col-narrow">{referralFor || '—'}</td>
              <td>
                {hasSend ? (
                  <>
                    <div className="fdt-prov-name">{sendUser || '—'}</div>
                    {sendSpec && <div className="fdt-prov-meta">{sendSpec}</div>}
                    {sendFac  && <div className="fdt-prov-meta">{sendFac}</div>}
                  </>
                ) : '—'}
              </td>
              <td>
                {hasCreate ? (
                  <>
                    <div className="fdt-prov-name">{createUser || '—'}</div>
                    {createSpec && <div className="fdt-prov-meta">{createSpec}</div>}
                    {createFac  && <div className="fdt-prov-meta">{createFac}</div>}
                  </>
                ) : <span className="fdt-zero">—</span>}
              </td>
              <td>
                <div className="fdt-prov-name">{f(r, 'receiving_user_name') || '—'}</div>
                <div className="fdt-prov-meta">{f(r, 'receiving_speciality') || ''}</div>
                <div className="fdt-prov-meta">{f(r, 'receiving_facility') || ''}</div>
              </td>
              <td>
                <div className="fdt-prov-name">{formatDicomLabel(r)}</div>
                {formatDocumentsLabel(r) && (
                  <div className="fdt-prov-meta">{formatDocumentsLabel(r)}</div>
                )}
              </td>
              <td>{formatTagsLabel(r)}</td>
              <td>{referralTypeLabel(r)}</td>
              <td><StatusBadge status={f(r, 'referral_status')} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function MessageRows({ rows, sortProps }) {
  // Cached rollup columns are per-channel sent/received counts. Show the
  // columns that have at least one non-zero value across the visible page
  // so the table doesn't sprout a forest of empty columns when one
  // channel dominates.
  const ALL_FIELDS = [
    { key: 'referral_messages_sent', label: 'Referral' },
    { key: 'vmail_messages_Sent',    label: 'VMail' },
    { key: 'vchat_messages_Sent',    label: 'VChat Sent' },
    { key: 'vchat_messages_received', label: 'VChat Received' },
    { key: 'vround_messages_sent',    label: 'VRounds' },
    { key: 'vtransfer_messages_sent', label: 'VTransfer' },
    { key: 'Other_messages_sent',     label: 'Other' },
  ];

  const activeFields = ALL_FIELDS.filter(col =>
    rows.some(r => Number(f(r, col.key)) > 0)
  );
  const cols = activeFields.length > 0 ? activeFields : ALL_FIELDS.slice(0, 3);

  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"     label="Date"     {...sortProps} />
          <SortableTh field="facility" label="Facility" {...sortProps} />
          {cols.map(c => (
            <SortableTh key={c.key} field={c.key} label={c.label} className="num" {...sortProps} />
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={cols.length + 2} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
            <td>
              <div className="fdt-prov-name">{f(r, 'facility_name') || '—'}</div>
              {f(r, 'facility_id') != null && (
                <div className="fdt-prov-meta">ID {f(r, 'facility_id')}</div>
              )}
            </td>
            {cols.map(c => (
              <td key={c.key} className="num">{fmtCount(f(r, c.key))}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StudyRows({ rows, sortProps }) {
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"     label="Date"     {...sortProps} />
          <SortableTh field="facility" label="Facility" {...sortProps} />
          <SortableTh field="sent"     label="Sent"     className="num" {...sortProps} />
          <SortableTh field="received" label="Received" className="num" {...sortProps} />
          <SortableTh field="total"    label="Total"    className="num" {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={5} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => {
          const sent = Number(f(r, 'study_sent')) || 0;
          const recv = Number(f(r, 'study_received')) || 0;
          return (
            <tr key={i}>
              <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
              <td>
                <div className="fdt-prov-name">{f(r, 'facility_name') || '—'}</div>
                {f(r, 'facility_id') != null && (
                  <div className="fdt-prov-meta">ID {f(r, 'facility_id')}</div>
                )}
              </td>
              <td className="num">{fmtCount(sent)}</td>
              <td className="num">{fmtCount(recv)}</td>
              <td className="num">{fmtCount(sent + recv)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function LoginRows({ rows, sortProps }) {
  // Cached login records are per-(facility, user, day). Show the per-platform
  // counters that actually have data on the visible page; always show "Total"
  // so the row is usable even when only an obscure platform is populated.
  const activePlatforms = LOGIN_PLATFORMS.filter(p =>
    rows.some(r => Number(f(r, p.key)) > 0)
  );
  const cols = activePlatforms.length > 0 ? activePlatforms : LOGIN_PLATFORMS.slice(0, 3);

  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"     label="Date"     {...sortProps} />
          <SortableTh field="facility" label="Facility" {...sortProps} />
          <SortableTh field="user"     label="User"     {...sortProps} />
          {cols.map(c => (
            <SortableTh key={c.key} field={c.key} label={c.label} className="num" {...sortProps} />
          ))}
          <SortableTh field="total" label="Total" className="num" {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={cols.length + 4} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => {
          let total = 0;
          for (const p of LOGIN_PLATFORMS) total += Number(f(r, p.key)) || 0;
          return (
            <tr key={i}>
              <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
              <td>
                <div className="fdt-prov-name">{f(r, 'facility_name') || '—'}</div>
                {f(r, 'facility_id') != null && (
                  <div className="fdt-prov-meta">ID {f(r, 'facility_id')}</div>
                )}
              </td>
              <td>
                <div className="fdt-prov-name">{f(r, 'user_display_name') || f(r, 'user_id') || '—'}</div>
                {f(r, 'user_id') && f(r, 'user_display_name') && (
                  <div className="fdt-prov-meta">ID {f(r, 'user_id')}</div>
                )}
              </td>
              {cols.map(c => (
                <td key={c.key} className="num">{fmtCount(f(r, c.key))}</td>
              ))}
              <td className="num"><strong>{fmtCount(total)}</strong></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

// ----------------------------------------------------------------------------
// V2.1 endpoint renderers (added 2026-05). Each shows the most useful columns
// from the per-row shape documented in API_REFERENCE.md / openapi.yaml.

function GroupReferralRows({ rows, sortProps }) {
  // Patient name and DOB are stripped server-side, so we don't even try to
  // render them — focus on the routing dimensions (referral_for, group,
  // wizard flow) and the latency signal (time-to-first-message).
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"        label="Date"             {...sortProps} />
          <SortableTh field="referralNo"  label="#"        className="num" {...sortProps} />
          <SortableTh field="referralFor" label="For"      className="fdt-col-narrow" {...sortProps} />
          <SortableTh field="group"       label="Group"    {...sortProps} />
          <th>Wizard?</th>
          <SortableTh field="firstMsg"    label="Time to first msg" className="num" {...sortProps} />
          <SortableTh field="status"      label="Status"   {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={7} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => {
          const groupName = f(r, 'receiving_group_name');
          const groupId = f(r, 'receiving_group_id');
          const isPrivate = Number(f(r, 'is_private_group')) === 1;
          const isWizard = Number(f(r, 'is_referral_wizard')) === 1;
          const created = ts(r);
          const firstMsg = f(r, 'referral_first_msg_created');
          let firstMsgLabel = '—';
          if (created && firstMsg) {
            const dc = new Date(created).getTime();
            const df = new Date(firstMsg).getTime();
            if (!Number.isNaN(dc) && !Number.isNaN(df) && df > dc) {
              const mins = Math.round((df - dc) / 60000);
              if (mins < 60) firstMsgLabel = `${mins}m`;
              else if (mins < 1440) firstMsgLabel = `${Math.round(mins / 60)}h`;
              else firstMsgLabel = `${Math.round(mins / 1440)}d`;
            }
          }
          return (
            <tr key={`${f(r, 'referral_id') || i}-${i}`}>
              <td className="fdt-cell-date">{fmtDate(created)}</td>
              <td className="num">{f(r, 'referral_number') || '—'}</td>
              <td className="fdt-col-narrow">{f(r, 'referral_for') || '—'}</td>
              <td>
                <div className="fdt-prov-name">{groupName || '—'}</div>
                {groupId != null && (
                  <div className="fdt-prov-meta">
                    ID {groupId}{isPrivate ? ' · private' : ''}
                  </div>
                )}
              </td>
              <td>{isWizard ? '✓' : <span className="fdt-zero">—</span>}</td>
              <td className="num">{firstMsgLabel}</td>
              <td><StatusBadge status={f(r, 'referral_status')} /></td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function FaxRows({ rows, sortProps }) {
  // fax_from / fax_to (phone numbers) are stripped server-side. The
  // remaining columns identify the row + the fax's pipeline state.
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"     label="Date"     {...sortProps} />
          <SortableTh field="status"   label="Status"   {...sortProps} />
          <SortableTh field="pages"    label="Pages"    className="num" {...sortProps} />
          <SortableTh field="facility" label="Facility ID" className="num" {...sortProps} />
          <SortableTh field="group"    label="Group ID"    className="num" {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={5} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={`${f(r, 'fax_id') || i}-${i}`}>
            <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
            <td><StatusBadge status={f(r, 'fax_status')} /></td>
            <td className="num">{fmtCount(f(r, 'num_pages'))}</td>
            <td className="num">{f(r, 'facility_id') ?? <span className="fdt-zero">—</span>}</td>
            <td className="num">{f(r, 'group_id') ?? <span className="fdt-zero">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FaxRecipientRows({ rows, sortProps }) {
  // This is the fax × user join table. Mostly useful when displayed
  // alongside fax records, but a flat table here is fine for VERA-style
  // ad-hoc inspection.
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"   label="Date"     {...sortProps} />
          <SortableTh field="faxId"  label="Fax ID"   className="num" {...sortProps} />
          <SortableTh field="userId" label="User ID"  className="num" {...sortProps} />
          <SortableTh field="isRead" label="Read?"    {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={4} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
            <td className="num">{f(r, 'fax_id') ?? '—'}</td>
            <td className="num">{f(r, 'user_id') ?? '—'}</td>
            <td>{Number(f(r, 'is_read')) === 1 ? '✓' : <span className="fdt-zero">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StudyDetailRows({ rows, sortProps }) {
  // PHI denylist applied server-side: study_instance_uid, dicom_study_id,
  // assession_number, referring_physician_name are NOT here. Show the
  // image-type columns that drive the "what % of CTs are CT Cardiac"
  // analysis the V2 endpoint was added for.
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date"        label="Date"        {...sortProps} />
          <SortableTh field="modalities"  label="Modality"    {...sortProps} />
          <SortableTh field="description" label="Description" {...sortProps} />
          <SortableTh field="facility"    label="Facility ID" className="num" {...sortProps} />
          <SortableTh field="user"        label="User ID"     className="num" {...sortProps} />
          <SortableTh field="processType" label="Process"     {...sortProps} />
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr><td colSpan={6} className="fdt-empty">No records.</td></tr>
        )}
        {rows.map((r, i) => (
          <tr key={`${f(r, 'study_id') || i}-${i}`}>
            <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
            <td>{f(r, 'modalities') || <span className="fdt-zero">—</span>}</td>
            <td>{f(r, 'study_description') || <span className="fdt-zero">—</span>}</td>
            <td className="num">{f(r, 'facility_id') ?? <span className="fdt-zero">—</span>}</td>
            <td className="num">{f(r, 'user_id') ?? <span className="fdt-zero">—</span>}</td>
            <td>{f(r, 'process_type') || <span className="fdt-zero">—</span>}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GenericRows({ rows, sortProps }) {
  // Defensive fallback for an unrecognised dataType. Sort by date only —
  // there's no schema to pivot off of.
  return (
    <table className="fdt-table fdt-formatted">
      <thead>
        <tr>
          <SortableTh field="date" label="Date" {...sortProps} />
          <th>Record</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => {
          const d = r?.data || r?.Data || {};
          const preview = Object.entries(d)
            .filter(([, v]) => v !== null && v !== undefined && v !== '')
            .slice(0, 6)
            .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`)
            .join(' · ');
          return (
            <tr key={i}>
              <td className="fdt-cell-date">{fmtDate(ts(r))}</td>
              <td className="fdt-prov-meta">{preview || '—'}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export default FormattedDataTable;
